import AddIcon from "@mui/icons-material/Add";
import { DND_TYPE, Rule, RuleGroup, useRuleStore } from "csi/middleware/attributeRule/RuleStore";
import { DropPlaceholderComponent, customViewFilterPaneDragHandleStyle, customViewFilterPaneDragPreviewComponent, customViewFilterPaneRenderListItem, DragHandle } from "csi/middleware/customView/data/dnd/data-management-tab-components";
import { getDialogZIndex } from "csi/utility/htmlUtility";
import DragPreviewLayer from "designSystem/dnd/components/DragPreviewLayer";
import { ColumnDropDividerWrapper, ColumnDropWrapper, DndStore, DndStoreContext, DragWrapper } from "designSystem/dnd/index";
import { useSyncLocalStore } from "designSystem/hooks";
import { Button, ConfirmDialog, FlexColumn, FlexRowCenter, SplitButton, Text } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";

const customViewFilterPaneDropTypes = [DND_TYPE.RULE, DND_TYPE.RULE_GROUP];

const CustomViewFilterPane: FunctionComponent = observer(() => {
	const {
		actions,
		addRule,
		addRuleGroup,
		canDrop,
		dropRule,
		isClearAllDialogOpen,
		onClickAddFilterButton,
		options,
		overRuleLimit,
		queryLevelName,
		relativeType,
		removeAllRules,
		ruleItems,
		setOpenClearAllDialog,
	} = useRuleStore();

	const dndStore = useSyncLocalStore(() => new DndStore<RuleGroup | Rule>(canDrop));
	const { dragItem } = dndStore;
	const dragItemIsInFilterPane = dragItem instanceof Rule || dragItem instanceof RuleGroup;

	return (
		<>
			{queryLevelName && (
				<Text semiBold text={queryLevelName} />
			)}
			{(options?.length > 0) && (
				<FlexColumn>
					<DndStoreContext.Provider value={dndStore}>
						<ColumnDropWrapper
							dropTypes={customViewFilterPaneDropTypes}
							isRootDropWrapper
							onDrop={dropRule}
						>
							<ColumnDropDividerWrapper
								dropPlaceholder={DropPlaceholderComponent}
								hideDraggedItem
							>
								{ruleItems.map((item, index) => (
									<DragWrapper
										canDrag
										data-csi-automation="CustomViewFilterGridItem"
										disableBgColorChangeOnDrag
										dragHandle={DragHandle}
										dragHandleStyle={customViewFilterPaneDragHandleStyle}
										dragItem={item}
										dragType={DND_TYPE.RULE}
										isFullScreen
										key={item.id}
									>
										{customViewFilterPaneRenderListItem(item)}
									</DragWrapper>
								))}
							</ColumnDropDividerWrapper>
						</ColumnDropWrapper>
						{dragItemIsInFilterPane && (<DragPreviewLayer previewComponent={customViewFilterPaneDragPreviewComponent} />)}
					</DndStoreContext.Provider>
					<FlexRowCenter
						data-csi-automation="CustomViewFilterPaneButtons"
						justifyContent="space-between"
						mt={2}
					>
						<FlexRowCenter gap={2}>
							{relativeType ? (
								<SplitButton
									buttons={actions}
									disabled={overRuleLimit}
									onClick={onClickAddFilterButton}
								/>
							)
								: (
									<Button
										design="outline"
										disabled={overRuleLimit}
										onClick={() => addRule()}
										res="CustomViewAddCustomViewFilter"
										startIcon={<AddIcon />}
									/>
								)}
							<Button
								design="outline"
								disabled={overRuleLimit}
								onClick={() => addRuleGroup()}
								res="CustomViewAddFilterGroup"
								startIcon={<AddIcon />}
							/>
						</FlexRowCenter>
						<Button
							design="link"
							disabled={ruleItems.length < 1}
							onClick={() => setOpenClearAllDialog(true)}
							res="CustomViewRemoveAllFilters"
						/>
						<ConfirmDialog
							category="error"
							confirmRes="Clear"
							messageRes="CustomViewClearAllFiltersConfirmMessage"
							onClose={() => setOpenClearAllDialog(false)}
							onConfirm={() => removeAllRules()}
							open={isClearAllDialogOpen}
							titleRes="CustomViewClearAllFiltersTitle"
							zIndex={getDialogZIndex()}
						/>
					</FlexRowCenter>
				</FlexColumn>
			)}
		</>
	);
});

export default CustomViewFilterPane;